import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/api";
import { FileText, CheckCircle, Clock, XCircle, BarChart2, Database, Upload, ArrowRight, AlertCircle } from "lucide-react";

const StatCard = ({ label, value, icon: Icon, color, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</p>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${color}`}>
        <Icon size={15} />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
  </div>
);

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then(r => setStats(r.data)).catch(() => {});
    api.get("/documents?limit=5").then(r => setRecentDocs(r.data.slice(0, 5))).catch(() => {});
    // Use /profile/company so regular users can see their own company credits
    api.get("/profile/company").then(r => setCompany(r.data)).catch(() => {});
  }, [user]);

  const statusColor = { approved: "text-emerald-600", review: "text-amber-600", processing: "text-blue-600", error: "text-red-600", rejected: "text-red-600", pending: "text-slate-500" };
  const statusBg = { approved: "bg-emerald-50", review: "bg-amber-50", processing: "bg-blue-50", error: "bg-red-50", rejected: "bg-red-50", pending: "bg-slate-50" };

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Good day, {user?.full_name?.split(" ")[0]}</h2>
        <p className="text-sm text-slate-500 mt-0.5">Here's what's happening with your documents today.</p>
      </div>

      {/* Credits bar (if company) */}
      {company && (
        <div data-testid="credits-bar" className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          {/* 80% warning */}
          {(() => {
            const pageUsedPct = company.page_credits > 0 ? (company.page_credits_used / company.page_credits) * 100 : 0;
            const aiUsedPct = company.ai_credits > 0 ? (company.ai_credits_used / company.ai_credits) * 100 : 0;
            const isWarning = pageUsedPct >= 80 || aiUsedPct >= 80;
            const isBlocked = pageUsedPct >= 100 || aiUsedPct >= 100;
            if (!isWarning) return null;
            return (
              <div className={`flex items-center gap-2 p-2.5 rounded-md text-xs font-medium ${isBlocked ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                <AlertCircle size={13} className="flex-shrink-0" />
                {isBlocked ? "Credit limit reached — document processing is paused. Contact your admin to top up." : "Approaching credit limit (80%+). Contact your admin to top up."}
              </div>
            );
          })()}
          <div className="flex gap-6 flex-wrap">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Page Credits</p>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (company.page_credits_used / Math.max(1, company.page_credits)) >= 1 ? "bg-red-500" :
                      (company.page_credits_used / Math.max(1, company.page_credits)) >= 0.8 ? "bg-amber-500" : "bg-[#006080]"
                    }`}
                    style={{ width: `${Math.min(100, (company.page_credits_used / Math.max(1, company.page_credits)) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-600 font-medium">{company.page_credits - company.page_credits_used} / {company.page_credits} left</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">AI Credits</p>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (company.ai_credits_used / Math.max(1, company.ai_credits)) >= 1 ? "bg-red-500" :
                      (company.ai_credits_used / Math.max(1, company.ai_credits)) >= 0.8 ? "bg-amber-500" : "bg-[#006080]"
                    }`}
                    style={{ width: `${Math.min(100, (company.ai_credits_used / Math.max(1, company.ai_credits)) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-600 font-medium">{company.ai_credits - company.ai_credits_used} / {company.ai_credits} left</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Documents" value={stats?.total_documents} icon={FileText} color="bg-[#E5F0F2] text-[#006080]" testId="stat-total-docs" />
        <StatCard label="Approved" value={stats?.approved} icon={CheckCircle} color="bg-emerald-50 text-emerald-600" testId="stat-approved" />
        <StatCard label="Pending Review" value={stats?.pending} icon={Clock} color="bg-amber-50 text-amber-600" testId="stat-pending" />
        <StatCard label="Schemas" value={stats?.schemas} icon={Database} color="bg-purple-50 text-purple-600" testId="stat-schemas" />
      </div>

      {/* Quick actions + Recent docs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/documents/upload" data-testid="quick-upload" className="flex items-center justify-between p-3 rounded-md hover:bg-slate-50 transition-colors group">
              <div className="flex items-center gap-3">
                <Upload size={16} className="text-[#006080]" />
                <span className="text-sm text-slate-700">Upload Document</span>
              </div>
              <ArrowRight size={14} className="text-slate-400 group-hover:text-[#006080]" />
            </Link>
            <Link to="/schemas/new" data-testid="quick-schema" className="flex items-center justify-between p-3 rounded-md hover:bg-slate-50 transition-colors group">
              <div className="flex items-center gap-3">
                <Database size={16} className="text-[#006080]" />
                <span className="text-sm text-slate-700">Create Schema</span>
              </div>
              <ArrowRight size={14} className="text-slate-400 group-hover:text-[#006080]" />
            </Link>
            <Link to="/analytics" data-testid="quick-analytics" className="flex items-center justify-between p-3 rounded-md hover:bg-slate-50 transition-colors group">
              <div className="flex items-center gap-3">
                <BarChart2 size={16} className="text-[#006080]" />
                <span className="text-sm text-slate-700">View Analytics</span>
              </div>
              <ArrowRight size={14} className="text-slate-400 group-hover:text-[#006080]" />
            </Link>
          </div>
        </div>

        {/* Recent documents */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Recent Documents</h3>
            <Link to="/documents" className="text-xs text-[#006080] hover:underline font-medium">View all</Link>
          </div>
          {recentDocs.length === 0 ? (
            <div className="text-center py-8">
              <img src="https://static.prod-images.emergentagent.com/jobs/db849cf8-d1c9-48aa-8157-7de403b6dbe4/images/07adfbd7cc6029680db469b48a393242a1ef5373fdb580dc49bda6c357c3a661.png" alt="Empty" className="w-20 h-20 object-cover mx-auto mb-3 opacity-50" />
              <p className="text-sm text-slate-500">No documents yet</p>
              <Link to="/documents/upload" className="text-xs text-[#006080] hover:underline mt-1 inline-block">Upload your first document</Link>
            </div>
          ) : (
            <div className="space-y-1">
              {recentDocs.map(doc => (
                <Link
                  key={doc.id}
                  to={`/documents/${doc.id}`}
                  data-testid={`recent-doc-${doc.id}`}
                  className="flex items-center justify-between p-2.5 rounded-md hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{doc.original_filename}</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${statusBg[doc.status]} ${statusColor[doc.status]}`}>
                    {doc.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
